import React, { useMemo, useState } from "react";
import { Button, Table } from "antd";
import type { TablePaginationConfig } from "antd";
import type {
  ColumnGroupType,
  ColumnType,
  FilterValue,
  SorterResult,
  TableCurrentDataSource,
} from "antd/es/table/interface";
import { CloseOutlined } from "@ant-design/icons";
import { checkFiltersAreEmpty } from "@/utils/tableConfigs/ActivitiesTableUtilFunctions";
import { getMedicalHistoryColumnConfig } from "@/utils/tableConfigs/MedicalHistoryConfigTable";
import InfoTooltip from "@/components/UI/InfoTooltip";

type MedicalHistoryRow = Record<string, unknown>;
type MedicalHistoryRowWithId = MedicalHistoryRow & { uniqueID: string };

const MedicalHistoryTable = ({ data = [] }: { data?: MedicalHistoryRow[] }) => {
  const [tableFilters, setTableFilters] = useState<
    Record<string, FilterValue | null>
  >({});
  const [filteredData, setFilteredData] = useState<MedicalHistoryRow[]>([]);
  const ellipsis = true;

  const dataWithIds = useMemo(
    () =>
      data.map((item, index) => ({
        ...item,
        uniqueID: `${item.subject}-${item.condition_name}-${index}`,
      })),
    [data],
  );

  const columns = getMedicalHistoryColumnConfig(
    data,
    tableFilters as Record<string, unknown[] | null>,
    ellipsis,
  ) as (
    | ColumnGroupType<MedicalHistoryRowWithId>
    | ColumnType<MedicalHistoryRowWithId>
  )[];

  const onChangeTableFilters = (
    pagination: TablePaginationConfig,
    filters: Record<string, FilterValue | null>,
    sorter:
      | SorterResult<MedicalHistoryRowWithId>
      | SorterResult<MedicalHistoryRowWithId>[],
    extra: TableCurrentDataSource<MedicalHistoryRowWithId>,
  ) => {
    setTableFilters(filters);
    if (checkFiltersAreEmpty(filters)) {
      setFilteredData([]);
    } else {
      setFilteredData(extra.currentDataSource);
    }
  };

  const handleClearAll = () => {
    setTableFilters({});
    setFilteredData([]);
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <h2
          className="text-[16px] font-bold mb-[15px]"
          data-testid="medical-history-table-subtitle"
        >
          Medical History
          <InfoTooltip
            className="ml-2"
            title="Subject's medical history collected at screening."
            data-testid="medical-history-subtitle-tooltip"
            fill="#00000073"
          />
        </h2>
        <Button
          type="text"
          className="p-0"
          icon={<CloseOutlined />}
          onClick={handleClearAll}
        >
          Clear all filters
        </Button>
      </div>
      <Table
        size="small"
        dataSource={dataWithIds}
        rowKey="uniqueID"
        columns={columns}
        onChange={onChangeTableFilters}
        pagination={false}
        scroll={{ x: "max-content" }}
        footer={() => {
          return (
            <div className="flex gap-4">
              <p
                className="font-bold"
                data-testid="medical-history-total-records"
              >
                Total records: {data.length}
              </p>
              {filteredData.length > 0 && (
                <p
                  className="font-bold"
                  data-testid="medical-history-selected-records"
                >
                  Selected records: {filteredData.length}
                </p>
              )}
            </div>
          );
        }}
      />
    </div>
  );
};

export default MedicalHistoryTable;
